import { Formik, FormikHelpers } from 'formik';
import { observer } from 'mobx-react-lite';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import {
  parsePhoneNumber,
  validatePhoneNumberLength,
} from 'libphonenumber-js/max';
import type { CountryCode } from 'libphonenumber-js/max';
import { useStore } from '../../stores/store';
import FormFieldText from '../../common/form-field-text/form-field-text.component';
import Button from '../../common/button/button.component';
import { EditFormProps } from '../../features/services/services-subsection/services-subsection.component';
import { ContactDetailsValues } from '../../models/contactDetailsValues';
import FormFieldPhone, {
  getCountryCallingCodeWrapper,
} from '../../common/form-field-phone/form-field-phone.component';
import {
  EMAIL_REGEX,
  VALIDATION_RULE_IS_NOT_CYRILLIC,
} from '../../constants/validation';
import {
  createValidationRule,
  isInitialValuesEqualCurrentValues,
} from '../../utils/forms/forms.utils';
import { useModal } from '../../common/modal';
import { Modals } from '../../constants/modals';

export const phoneRegex = /^[0-9]{11,16}$/;

const phoneValidation = (phoneField: string, countryCodeField: string) =>
  Yup.string()
    .required('this_field_cannot_be_empty')
    .test('phone', 'invalid_phone_number', (_, ctx) => {
      const countryCode = ctx.parent[countryCodeField];
      const phoneNumber = ctx.parent[phoneField];

      if (!phoneNumber) return true;

      const lengthValidationResult = countryCode
        ? validatePhoneNumberLength(
            phoneNumber,
            countryCode.toLocaleUpperCase() as CountryCode
          )
        : validatePhoneNumberLength(`+${phoneNumber}`);

      if (lengthValidationResult === 'TOO_SHORT') {
        return ctx.createError({ message: 'phone_number_too_short' });
      }

      if (lengthValidationResult) {
        return ctx.createError({ message: 'invalid_phone_number' });
      }

      try {
        if (countryCode) {
          const combinedValue = `+${getCountryCallingCodeWrapper(countryCode)}${phoneNumber}`;
          const parsed = parsePhoneNumber(combinedValue);

          return (
            parsed.isValid() && parsed.number.length === combinedValue.length
          );
        }

        const combinedValue = `+${phoneNumber}`;
        const parsed = parsePhoneNumber(combinedValue);

        return (
          parsed.isValid() && parsed.number.length === combinedValue.length
        );
      } catch {
        return ctx.createError({ message: 'invalid_phone_number' });
      }
    });

export const emailValidationSchema = createValidationRule(
  VALIDATION_RULE_IS_NOT_CYRILLIC
).matches(EMAIL_REGEX, {
  message: 'wrong_email_format',
  excludeEmptyString: true,
});

export const contactDetailsValidationSchema = Yup.object({
  phone: phoneValidation('phone', 'phone_country_code'),

  phone_country_code: Yup.string().notRequired(),

  email: emailValidationSchema.required('this_field_cannot_be_empty'),
});

export function removeCountryCallingCodeFromPhone(
  phone?: string | null,
  countryCode?: string | null
): string | undefined {
  if (phone && countryCode) {
    const callingCode = getCountryCallingCodeWrapper(countryCode);
    if (callingCode && phone.startsWith(callingCode)) {
      return phone.substring(callingCode.length);
    }
    return phone;
  }

  return undefined;
}

const ContactDetailsEditForm = ({ onCancel }: EditFormProps) => {
  const {
    userStore: { user, isLoadingUser, updatePhone, setPhoneForVerification },
    commonStore: { toastSuccess },
    localizationsStore: { availableCountries, selectedCountry },
  } = useStore();

  const { openModal } = useModal();

  const { t } = useTranslation();

  const handleFormSubmit = async (
    formValues: ContactDetailsValues,
    formikHelpers: FormikHelpers<ContactDetailsValues>
  ) => {
    const newPhone = {
      phone_number: `${getCountryCallingCodeWrapper(formValues.phone_country_code)}${formValues.phone}`,
      country_code: formValues.phone_country_code,
    };

    const { requiresVerification, error } = await updatePhone(newPhone);

    if (error) {
      formikHelpers.setFieldError('phone', error);
      return;
    }

    const onSuccessfulUpdate = async () => {
      onCancel && onCancel();
      formikHelpers.resetForm();
    };

    if (requiresVerification) {
      setPhoneForVerification(newPhone);

      openModal({
        id: Modals.USER_SETTINGS_PHONE_VERIFICATION,
        name: Modals.USER_SETTINGS_PHONE_VERIFICATION,
        props: {
          onSuccessfulUpdate,
        },
      });

      return;
    }

    toastSuccess(t('data_updated_successfully'));
    onSuccessfulUpdate();
  };

  const initialValues: ContactDetailsValues = {
    phone:
      removeCountryCallingCodeFromPhone(
        user?.phone?.phone_number,
        user?.phone?.country_code
      ) ?? '',
    phone_country_code: user?.phone?.country_code ?? 'US',
    email: user?.email ?? '',
  };

  return (
    <Formik
      enableReinitialize
      validationSchema={contactDetailsValidationSchema}
      initialValues={initialValues}
      onSubmit={(values, helpers) => {
        if (isInitialValuesEqualCurrentValues(initialValues, values)) {
          onCancel && onCancel();
          return;
        }
        handleFormSubmit(values, helpers);
      }}>
      {({ handleSubmit, isValid }) => (
        <form
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}>
          <div className='inputs'>
            <FormFieldText
              name='email'
              placeholder=''
              label={`${t('email')}`}
              inputMode='email'
              disabled
              maxLength={50}
              showMaxLengthCounter
            />

            <FormFieldPhone
              phoneFieldName='phone'
              codeFieldName='phone_country_code'
              placeholder='(555) 000 - 0000'
              label={`${t('phone_number')}`}
              allowedPhoneCountries={
                availableCountries?.map((x) => x.countryDetails) ?? []
              }
              defaultCountryCode={selectedCountry?.country_code}
            />
          </div>

          <div className='buttons'>
            <Button
              appearance='secondary'
              disabled={isLoadingUser}
              onClick={() => onCancel && onCancel()}>
              {t('cancel')}
            </Button>
            <Button
              isLoading={isLoadingUser}
              disabled={!isValid}
              type='submit'>
              {t('save_changes')}
            </Button>
          </div>
        </form>
      )}
    </Formik>
  );
};

export default observer(ContactDetailsEditForm);
